import Image from 'next/image';
import React, { useRef } from 'react';

import { akamaiLoader } from '@/utils/akamaiLoader';
import { getLinkAriaLabel } from '@/utils/urlUtils';

import { useRouter } from '@/hooks/useRouter';
import { searchStateActions } from '@/states/searchState';

import { getLogoAlt, getLogoHref, getSizes, getSrc } from './helper';

import * as S from './Logo.styles';

export interface LogoProps {
  disableClick?: boolean;
  heightDesktop?: number | null;
  heightMobile?: number | null;
  isHeader?: boolean;
  link?: string | null;
  src?: string | null;
  variant?: string;
  widthDesktop?: number | null;
  widthMobile?: number | null;
}

export const Logo = ({
  disableClick,
  heightDesktop,
  heightMobile,
  isHeader = false,
  link = '/',
  src,
  variant,
  widthDesktop,
  widthMobile,
}: LogoProps) => {
  const finalVariant = variant ?? 'logo140';
  const router = useRouter();
  const loadingRef = useRef<boolean>(false);
  const href = getLogoHref(router.locale, disableClick, link);
  const isExternalLink =
    href.startsWith('http://') || href.startsWith('https://');

  const handleCloseOverlay = () => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    searchStateActions.changeOverlayStatus('CLOSE');
    router.push(href).then(() => {
      loadingRef.current = false;
    });
  };

  const size = getSizes(finalVariant, false);
  const mobileSize = getSizes(finalVariant, true);
  const imageSrc = src ?? getSrc(finalVariant);

  return (
    <S.LogoBox
      $disableClick={disableClick}
      aria-label={!disableClick ? getLinkAriaLabel(href) : undefined}
      as={disableClick ? 'div' : 'a'}
      href={!disableClick ? href : undefined}
      onClick={(e: React.MouseEvent) => {
        if (isExternalLink && !disableClick) return;
        e.preventDefault();
        if (disableClick) return;
        handleCloseOverlay();
      }}
      target={!disableClick && isExternalLink ? '_blank' : undefined}
    >
      <S.Wrapper>
        <Image
          alt={getLogoAlt(href, disableClick)}
          data-test-id='LogoImage1'
          height={heightDesktop ?? size.height}
          loader={akamaiLoader}
          priority={isHeader}
          src={imageSrc}
          width={widthDesktop ?? size.width}
        />
      </S.Wrapper>
      <S.WrapperInline>
        <Image
          alt={getLogoAlt(href, disableClick)}
          data-test-id='LogoImage2'
          height={heightMobile ?? mobileSize.height}
          loader={akamaiLoader}
          priority={isHeader}
          src={imageSrc}
          width={widthMobile ?? mobileSize.width}
        />
      </S.WrapperInline>
    </S.LogoBox>
  );
};

Logo.displayName = 'Logo';
